import { useEffect, lazy, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { isBlogHost } from "./lib/siteUrls";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { Toaster } from "./components/ui/toaster";
import { PageLoader } from "./components/PageLoader";
import { RouteTracker } from "./components/RouteTracker";
import { CookieConsent } from "./components/CookieConsent";
import { isDebug } from "./lib/analytics";

const Home = lazy(() => import("./pages/Home"));
const Entrepreneurs = lazy(() => import("./pages/Entrepreneurs"));
const Supporters = lazy(() => import("./pages/Supporters"));
const Partnerships = lazy(() => import("./pages/Partnerships"));
const About = lazy(() => import("./pages/About"));
const Donate = lazy(() => import("./pages/Donate"));
const SuccessStoriesPage = lazy(() => import("./pages/SuccessStoriesPage"));
const FounderStory = lazy(() => import("./pages/FounderStory"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const Communities = lazy(() => import("./pages/Communities"));
const Launchpad = lazy(() => import("./pages/Launchpad"));
const OurTeam = lazy(() => import("./pages/OurTeam"));
const Accenture = lazy(() => import("./pages/Accenture"));
const AccentureCaseStudy = lazy(() => import("./pages/AccentureCaseStudy"));
const PoweredBy = lazy(() => import("./pages/PoweredBy"));
const MentorshipGuide = lazy(() => import("./pages/MentorshipGuide"));
const GiveViaDAF = lazy(() => import("./pages/GiveViaDAF"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const UKLaunch = lazy(() => import("./pages/UKLaunch"));
const NotFound = lazy(() => import("./pages/NotFound"));
const BusinessResources = lazy(() => import("./pages/BusinessResources"));
const EntrepreneurGrants = lazy(() => import("./pages/EntrepreneurGrants"));
const JoinFree = lazy(() => import("./pages/JoinFree"));
const Membership = lazy(() => import("./pages/Membership"));
const FriendsFamilyFund = lazy(() => import("./pages/FriendsFamilyFund"));
const StartupGuide = lazy(() => import("./pages/StartupGuide"));
const SlugRouter = lazy(() => import("./pages/SlugRouter"));
const SurveyFeedback = lazy(() => import("./pages/SurveyFeedback"));
const SurveyResults = lazy(() => import("./pages/SurveyResults"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const LegacyPage = lazy(() => import("./pages/LegacyPage"));
const PartnerPage = lazy(() => import("./pages/PartnerPage"));
const AnalyticsDebugPanel = lazy(() => import("./components/AnalyticsDebugPanel"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function Shell() {
  const { pathname } = useLocation();
  const bare = pathname === "/feedback2026" || pathname.startsWith("/feedback2026/");
  return (
    <>
      {!bare && <Navbar />}
      <main>
        <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/feedback2026" element={<SurveyFeedback />} />
          <Route path="/feedback2026/results" element={<SurveyResults />} />
          <Route path="/" element={isBlogHost() ? <Blog /> : <Home />} />
          <Route path="/entrepreneurs" element={<Entrepreneurs />} />
          <Route path="/supporters" element={<Supporters />} />
          <Route path="/partnerships" element={<Partnerships />} />
          <Route path="/about" element={<About />} />
          <Route path="/donate" element={<Donate />} />
          <Route path="/success-stories" element={<SuccessStoriesPage />} />
          <Route path="/success-stories/:slug" element={<FounderStory />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/communities" element={<Communities />} />
          <Route path="/launchpad" element={<Launchpad />} />
          <Route path="/our-team" element={<OurTeam />} />
          <Route path="/mentorship-guide" element={<MentorshipGuide />} />
          <Route path="/daf" element={<GiveViaDAF />} />
          <Route path="/DAF" element={<Navigate to="/daf" replace />} />
          <Route path="/powered-by" element={<PoweredBy />} />
          <Route path="/accenture" element={<Accenture />} />
          <Route path="/accenture-case-study" element={<AccentureCaseStudy />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/uk-launch" element={<UKLaunch />} />
          <Route path="/blog/:legacy" element={<BlogPost />} />
          <Route path="/business-resources" element={<BusinessResources />} />
          <Route path="/entrepreneur-grants" element={<EntrepreneurGrants />} />
          <Route path="/join-skys-the-limit-free" element={<JoinFree />} />
          <Route path="/membership" element={<Membership />} />
          <Route path="/friends-family-fund" element={<FriendsFamilyFund />} />
          <Route path="/privacy-policy" element={<LegalPage />} />
          <Route path="/terms-of-use" element={<LegalPage />} />
          <Route path="/community-guidelines" element={<LegalPage />} />
          <Route path="/stay-motivated-while-you-grow-your-business" element={<LegacyPage />} />
          <Route path="/bring-joy-into-your-life-and-take-your-business-to-the-next-level" element={<LegacyPage />} />
          <Route path="/exploring-financial-freedom-for-yourself-and-your-business" element={<LegacyPage />} />
          <Route path="/build-off-your-successes-to-achieve-your-dreams" element={<LegacyPage />} />
          <Route path="/mentor-dynamics-the-transformative-power-of-volunteer-business-mentoring-programs" element={<LegacyPage />} />
          <Route path="/tulsa-stl-community" element={<LegacyPage />} />
          <Route path="/tulsa-funding" element={<LegacyPage />} />
          <Route path="/uk-grants" element={<LegacyPage />} />
          <Route path="/entrepreneurs-uk" element={<LegacyPage />} />
          <Route path="/volunteers-uk" element={<LegacyPage />} />
          <Route path="/rise-up-refugees-uk" element={<LegacyPage />} />
          <Route path="/skys-the-limit-rise-up-refugee" element={<LegacyPage />} />
          <Route path="/philanthropy-partners" element={<LegacyPage />} />
          <Route path="/theory-of-change" element={<LegacyPage />} />
          <Route path="/marketplace" element={<LegacyPage />} />
          <Route path="/marketplace-live" element={<LegacyPage />} />
          <Route path="/marketplace-live-application" element={<LegacyPage />} />
          <Route path="/inspire-others-and-magnify-your-impact" element={<LegacyPage />} />
          <Route path="/be-featured" element={<LegacyPage />} />
          <Route path="/limitless-top-10-story-features" element={<LegacyPage />} />
          <Route path="/thenumbersgame" element={<LegacyPage />} />
          <Route path="/skys-the-limit-dao-2023" element={<LegacyPage />} />
          <Route path="/tulsa-idea-challenge-2023" element={<LegacyPage />} />
          <Route path="/chat-for-change" element={<LegacyPage />} />
          <Route path="/livepitchmarch" element={<LegacyPage />} />
          <Route path="/milestones" element={<LegacyPage />} />
          <Route path="/video-voice" element={<LegacyPage />} />
          <Route path="/campaign-video" element={<LegacyPage />} />
          <Route path="/moneytalks" element={<LegacyPage />} />
          <Route path="/contact" element={<LegacyPage />} />
          <Route path="/entrepreneur-talk-to-someone" element={<LegacyPage />} />
          <Route path="/schwab" element={<PartnerPage />} />
          <Route path="/pnc" element={<PartnerPage />} />
          <Route path="/citi" element={<PartnerPage />} />
          <Route path="/hp-partner" element={<PartnerPage />} />
          <Route path="/own-a-gym" element={<StartupGuide />} />
          <Route path="/start-landscaping-business" element={<StartupGuide />} />
          <Route path="/start-your-online-business" element={<StartupGuide />} />
          <Route path="/start-your-business" element={<StartupGuide />} />
          <Route path="/mentors-the-key-to-success" element={<StartupGuide />} />
          <Route path="/how-to-become-a-nurse-entrepreneur" element={<StartupGuide />} />
          <Route path="/start-your-nail-business-in-2024-expert-tips-guidance" element={<StartupGuide />} />
          <Route path="/start-a-successful-clothing-business-with-expert-help" element={<StartupGuide />} />
          <Route path="/start-a-makeup-line" element={<StartupGuide />} />
          <Route path="/get-help-starting-a-lip-gloss-line-2024-expert-tips" element={<StartupGuide />} />
          <Route path="/start-a-food-truck" element={<StartupGuide />} />
          <Route path="/sticker-business" element={<StartupGuide />} />
          <Route path="/start-beauty-business" element={<StartupGuide />} />
          <Route path="/start-a-virtual-assistant-business" element={<StartupGuide />} />
          <Route path="/start-a-tshirt-business" element={<StartupGuide />} />
          <Route path="/start-a-car-wash" element={<StartupGuide />} />
          <Route path="/social-media-consultant" element={<StartupGuide />} />
          <Route path="/sneaker-business" element={<StartupGuide />} />
          <Route path="/print-on-demand-business" element={<StartupGuide />} />
          <Route path="/phone-case-business" element={<StartupGuide />} />
          <Route path="/personal-shopper-business" element={<StartupGuide />} />
          <Route path="/own-a-bar" element={<StartupGuide />} />
          <Route path="/open-daycare-center" element={<StartupGuide />} />
          <Route path="/open-a-restaurant" element={<StartupGuide />} />
          <Route path="/lash-business" element={<StartupGuide />} />
          <Route path="/hair-wig-and-weave-business" element={<StartupGuide />} />
          <Route path="/how-to-start-a-doula-or-midwife-business" element={<StartupGuide />} />
          <Route path="/how-to-make-money-as-a-vacation-rental-host" element={<StartupGuide />} />
          <Route path="/how-to-become-a-consultant" element={<StartupGuide />} />
          <Route path="/homecare-business" element={<StartupGuide />} />
          <Route path="/auto-repair-shop" element={<StartupGuide />} />
          <Route path="/start-a-skincare-business" element={<StartupGuide />} />
          <Route path="/start-a-pet-care-business" element={<StartupGuide />} />
          <Route path="/:slug" element={<SlugRouter />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </main>
      {!bare && <Footer />}
      <Toaster />
      <CookieConsent />
      {isDebug() && <Suspense fallback={null}><AnalyticsDebugPanel /></Suspense>}
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <RouteTracker />
        <Shell />
      </BrowserRouter>
    </div>
  );
}

export default App;
